import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { SpaceFileIntent } from 'shared';

import { BackendRoute } from '../../../config/index';
import { useLocale } from '../../../hooks/use-locale.hook';
import { ApiService } from '../../../services/index';
import { SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { TreeSpaceRef } from '../types/data-room.types';
import { DataRoomViewer, useDataRoomViewer } from './use-recipient-view.hook';

/**
 * One file out of a workspace or a data room. Which route carries it is decided before the request,
 * like the rest of the module: the owner's module routes refuse a recipient, and a recipient's route
 * is scoped to the room they were shared. The same file asked for again while its request is out
 * is not asked for twice.
 */
export const useDownloadSpaceFile = () => {
  const viewer = useDataRoomViewer();
  const { messagesLocale } = useLocale();
  const downloading = useRef(new Set<string>());

  return useCallback(
    async ({ fileId, space }: { fileId: string; space: TreeSpaceRef }) => {
      if (viewer === DataRoomViewer.UNRESOLVED || downloading.current.has(fileId)) return;

      downloading.current.add(fileId);

      try {
        await ApiService.downloadFile(
          {
            endpoint:
              viewer === DataRoomViewer.RECIPIENT
                ? BackendRoute.MY_DATA_ROOMS
                : SPACE_BACKEND_ROUTE[space.kind],
            routePath: [space.id, 'files', fileId, 'download'],
          },
          { queryParams: { intent: SpaceFileIntent.DOWNLOAD } },
        );
      } catch {
        toast.error(messagesLocale['FILE_DOWNLOAD_FAILED']);
      } finally {
        downloading.current.delete(fileId);
      }
    },
    [messagesLocale, viewer],
  );
};
